import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ConfirmDialogComponent } from '../../../components/confirm-dialog/confirm-dialog.component';
import { ObjectUsageListComponent } from '../object-usage-list/object-usage-list.component';
import { ObjectMappingService } from '../../../services/object-mapping.service';
import { RenameResult } from '../../../models/object-mapping.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { serverMessage } from '../server-message';
import { isValidObjectName } from '../object-names';

/**
 * Umbenennen eines Eintrags oder, ohne `entryId`, einer ganzen Bibliothek (NEX-1278):
 * erst die Vorschau (`preview=true`), dann nach Bestaetigung ausfuehren (`preview=false`).
 *
 * Design-Luecke: Das Bundle hat keinen Entwurf dafuer. Aufbau im Stil des vorhandenen
 * Bestaetigungsdialogs.
 */
@Component({
  selector: 'app-object-rename-dialog',
  imports: [
    TranslatePipe,
    MatButton,
    MatFormField,
    MatLabel,
    MatInput,
    MatProgressSpinner,
    ConfirmDialogComponent,
    ObjectUsageListComponent,
  ],
  templateUrl: './object-rename-dialog.component.html',
  styleUrl: './object-rename-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectRenameDialogComponent {
  private readonly service = inject(ObjectMappingService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly mappingId = input.required<string>();
  readonly entryId = input<string | undefined>(undefined);
  readonly currentName = input.required<string>();
  readonly renamed = output<RenameResult>();
  readonly cancelled = output<void>();

  readonly fieldLengths = FIELD_LENGTHS;
  readonly name = signal('');
  readonly preview = signal<RenameResult | null>(null);
  readonly previewLoading = signal(false);
  readonly applying = signal(false);
  readonly error = signal<string | null>(null);

  readonly isLibrary = computed(() => !this.entryId());
  readonly nameValid = computed(() => {
    const name = this.name();
    return isValidObjectName(name) && name !== this.currentName();
  });
  readonly showInvalid = computed(() => this.name().length > 0 && !this.nameValid());
  readonly canPreview = computed(() => this.nameValid() && !this.previewLoading() && !this.applying());
  readonly canApply = computed(() => this.preview() !== null && !this.previewLoading());

  private readonly nameInput = viewChild.required<ElementRef<HTMLInputElement>>('nameInput');
  private request: Subscription | null = null;

  constructor() {
    afterNextRender(() => this.nameInput().nativeElement.focus());
    this.destroyRef.onDestroy(() => this.request?.unsubscribe());
  }

  onNameInput(event: Event): void {
    this.name.set((event.target as HTMLInputElement).value);
    this.request?.unsubscribe();
    this.previewLoading.set(false);
    this.preview.set(null);
    this.error.set(null);
  }

  loadPreview(): void {
    if (!this.canPreview()) return;
    this.previewLoading.set(true);
    this.preview.set(null);
    this.error.set(null);
    this.request = this.rename(true).subscribe({
      next: (result) => {
        this.preview.set(result);
        this.previewLoading.set(false);
      },
      error: (err: unknown) => {
        this.error.set(this.messageFor(err));
        this.previewLoading.set(false);
      },
    });
  }

  apply(): void {
    if (!this.canApply() || this.applying()) return;
    this.applying.set(true);
    this.error.set(null);
    this.rename(false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.applying.set(false);
          this.renamed.emit(result);
        },
        error: (err: unknown) => {
          // Die Vorschau kann veraltet sein (409 bei geaendertem Index): neu anfordern lassen.
          this.preview.set(null);
          this.error.set(this.messageFor(err));
          this.applying.set(false);
        },
      });
  }

  private rename(preview: boolean) {
    return this.service.rename(this.projectId(), this.mappingId(), this.entryId(), this.name(), preview);
  }

  private messageFor(err: unknown): string {
    const fromServer = serverMessage(err);
    if (fromServer) return fromServer;
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    const key =
      status === 400
        ? 'objectMappings.rename.invalidName'
        : status === 409
          ? 'objectMappings.rename.conflict'
          : 'objectMappings.rename.error';
    return this.translate.instant(key);
  }
}
